/* Tienda Novale – <internal-video>: Shopify-hosted video with play/sound buttons and a timeline. */
if (!customElements.get('internal-video')) {
  class InternalVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.playButton = this.querySelector('.internal-video__play');
      this.soundButton = this.querySelector('.internal-video__sound');
      this.timeline = this.querySelector('.internal-video__timeline');
      this.loaded = false;

      if (this.playButton) this.playButton.addEventListener('click', () => this.toggle());
      this.video.addEventListener('click', () => {
        if (this.dataset.autoplay !== 'true' || this.playButton) this.toggle();
      });
      if (this.soundButton) this.soundButton.addEventListener('click', () => this.toggleSound());
      this.video.addEventListener('ended', () => this.classList.remove('internal-video--playing'));
      this.video.addEventListener('waiting', () => this.classList.add('internal-video--loading'));
      ['canplay', 'playing'].forEach((e) => this.video.addEventListener(e, () => this.classList.remove('internal-video--loading')));

      if (this.timeline) {
        this.video.addEventListener('timeupdate', () => this.updateTimeline());
        const seek = (event) => {
          const rect = this.timeline.getBoundingClientRect();
          const x = (event.touches ? event.touches[0].clientX : event.clientX) - rect.left;
          const ratio = Math.min(1, Math.max(0, x / rect.width));
          if (this.video.duration) this.video.currentTime = ratio * this.video.duration;
        };
        this.timeline.addEventListener('pointerdown', (event) => {
          this.dragging = true;
          seek(event);
        });
        window.addEventListener('pointermove', (event) => this.dragging && seek(event));
        window.addEventListener('pointerup', () => (this.dragging = false));
      }

      if (this.dataset.autoplay === 'true') {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.play();
              else if (!this.video.paused) this.video.pause();
            });
          },
          { threshold: 0.05 }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    load() {
      if (this.loaded) return;
      this.video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      this.video.load();
      this.loaded = true;
    }

    play() {
      this.load();
      const promise = this.video.play();
      this.classList.add('internal-video--playing');
      if (promise) {
        promise.catch(() => {
          this.classList.remove('internal-video--playing');
          if (this.playButton) this.playButton.hidden = false;
        });
      }
    }

    toggle() {
      if (this.video.paused) {
        document.querySelectorAll('internal-video').forEach((other) => {
          if (other !== this && other.video && !other.video.paused && other.dataset.autoplay !== 'true') other.video.pause();
        });
        this.play();
      } else {
        this.video.pause();
        this.classList.remove('internal-video--playing');
      }
    }

    toggleSound() {
      this.video.muted = !this.video.muted;
      this.classList.toggle('internal-video--muted', this.video.muted);
      if (!this.video.muted && this.video.paused) this.play();
    }

    updateTimeline() {
      if (!this.video.duration) return;
      const done = (this.video.currentTime / this.video.duration) * 100;
      this.style.setProperty('--completed', `${done}%`);
      this.timeline.setAttribute('aria-valuenow', Math.round(done));
    }

    /* Called by sliders when the slide leaves the viewport. */
    onSlideHidden() {
      if (this.dataset.onHidden === 'mute') {
        this.video.muted = true;
        this.classList.add('internal-video--muted');
      } else {
        this.video.pause();
        this.classList.remove('internal-video--playing');
      }
    }
  }
  customElements.define('internal-video', InternalVideo);
}
